import {
  ElementRef,
  FC,
  forwardRef,
  SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useFormikContext } from 'formik';
import useGetTokens from 'modules/account/settings/hooks/useGetTokens';
import { IoMdClose } from 'react-icons/io';
import { IoVideocam } from 'react-icons/io5';
import { MdContentCopy } from 'react-icons/md';
import { Button } from 'ui/components/common';
import { toastSuccess } from 'ui/partials/Toast';
import { STATUS } from 'utils/constants';
import { cn, copyToClipboard } from 'utils/helpers';
import { FaCaretDown, FaCaretUp } from 'react-icons/fa';
import { Token } from 'utils/types';
import useOnClickOutside from 'hooks/useOnClickOutside';
import { Popover } from '@headlessui/react';
import { ZoomIcon, GoogleMeetIcon } from 'ui/components/icon';

import { useCreateMeetingLink } from '../../hooks';
import { EVENT_FORM } from '../schema';

interface ACCOUNT_OPTIONS_PROPS {
  options: Token[];
  onClick: (e: any) => any;
}

const AccountOptions = forwardRef<ElementRef<'div'>, ACCOUNT_OPTIONS_PROPS>(
  ({ options, onClick }, ref) => (
    <Popover ref={ref}>
      <Popover.Panel
        static
        className={cn(
          'absolute',
          'rounded-md',
          'border',
          'bg-[#FFFFFF]',
          'border-disabled',
          'z-[99]',
          'right-0',
          'w-full',
          'top-10',
        )}
      >
        {options.map((token: Token) => (
          <button
            className={cn(
              'px-4',
              'py-2',
              'text-sm',
              'text-secondary-base',
              'hover:text-primary-base',
              'w-full',
              'text-left',
            )}
            type="button"
            onClick={() => onClick(token)}
          >
            {token?.meta?.emailAddress}
          </button>
        ))}
      </Popover.Panel>
    </Popover>
  ),
);

const AddLink: FC = () => {
  const [isShow, setIsShow] = useState<SetStateAction<boolean>>(false);
  const panelRef = useRef<ElementRef<'div'>>(null);
  useOnClickOutside(panelRef, () => setIsShow(false));

  const { setFieldValue, isSubmitting, values } =
    useFormikContext<EVENT_FORM>();

  const { data: tokensData } = useGetTokens({
    source: ['GOOGLE', 'ZOOM'],
    scopes: ['MEETING'],
    status: STATUS.ACTIVE,
  });

  const { mutate: createMeetingLink, isLoading: isMeetingLinkLoading } =
    useCreateMeetingLink({
      onSuccess: ({ data }) => {
        toastSuccess('Successfully created a meeting link.');
        setFieldValue('eventUrl', data.eventUrl);
      },
    });

  const isGoogleLink = values?.eventUrl?.includes('google');
  const isZoomLink = values?.eventUrl?.includes('zoom');

  const handleCreateMeetingLink = useCallback(
    (token: Token) => {
      createMeetingLink(token?._id);

      setIsShow(false);
    },
    [setIsShow, createMeetingLink],
  );

  const googleMeet = useMemo(() => {
    if (!tokensData?.data?.find((rec) => rec?.source === 'GOOGLE')) return null;

    return (
      <div className="relative">
        <Button
          onClick={() => setIsShow(!isShow)}
          disabled={isMeetingLinkLoading || isSubmitting}
          className={cn(
            'font-semibold',
            'p-4',
            '!text-secondary-dark',
            isMeetingLinkLoading || isSubmitting
              ? 'bg-disabled !text-secondary-base'
              : '',
          )}
          size="sm"
          intent="tertiary"
          icon={isShow ? FaCaretUp : FaCaretDown}
          right
          label={
            <div className="flex space-x-2 items-center">
              <GoogleMeetIcon />
              <div>ADD GOOGLE MEET LINK</div>
            </div>
          }
        />
        {isShow && (
          <AccountOptions
            options={
              tokensData?.data?.filter(
                (rec) => rec?.source === 'GOOGLE',
              ) as Token[]
            }
            onClick={handleCreateMeetingLink}
          />
        )}
      </div>
    );
  }, [
    tokensData,
    isShow,
    setIsShow,
    isMeetingLinkLoading,
    handleCreateMeetingLink,
    isSubmitting,
  ]);

  const zoomMeet = useMemo(() => {
    if (!tokensData?.data?.find((rec) => rec.source === 'ZOOM')) return null;

    return (
      <div className="relative">
        <Button
          onClick={() => setIsShow(!isShow)}
          disabled={isMeetingLinkLoading || isSubmitting}
          className={cn(
            'font-semibold',
            'p-4',
            '!text-secondary-dark',
            isMeetingLinkLoading || isSubmitting
              ? 'bg-disabled !text-secondary-base'
              : '',
          )}
          icon={isShow ? FaCaretUp : FaCaretDown}
          right
          label={
            <div className="flex space-x-2 items-center">
              <ZoomIcon />
              <div>ADD ZOOM LINK</div>
            </div>
          }
        />
        {isShow && (
          <AccountOptions
            ref={panelRef}
            options={
              tokensData?.data?.filter(
                (rec) => rec.source === 'ZOOM',
              ) as Token[]
            }
            onClick={handleCreateMeetingLink}
          />
        )}
      </div>
    );
  }, [
    tokensData,
    isShow,
    isMeetingLinkLoading,
    handleCreateMeetingLink,
    isSubmitting,
  ]);

  return (
    <div className="flex items-center py-3">
      <IoVideocam className="mr-4 h-5 w-5 text-content flex-shrink-0" />
      {!tokensData?.data.length && !values?.eventUrl && (
        <button
          type="button"
          onClick={() => {
            window.location.href = '/settings/general/calendar';
          }}
          className={cn(
            'font-semibold',
            'text-sm',
            'text-blue',
            'leading-[22px]',
            'whitespace-pre-wrap',
            'hover:underline',
          )}
        >
          Connect your Google or Zoom accounts to generate a meeting link.
        </button>
      )}
      {!values?.eventUrl ? (
        <div className="flex items-center gap-2 w-full flex-1">
          {googleMeet}
          {zoomMeet}
        </div>
      ) : (
        <div className="flex w-full items-center gap-2">
          {isGoogleLink && (
            <Button
              icon={GoogleMeetIcon}
              left
              label="JOIN MEETING"
              onClick={() => window.open(values?.eventUrl ?? '')}
              className="flex-shrink-0 py-2"
              intent="secondary"
            />
          )}
          {isZoomLink && (
            <Button
              icon={ZoomIcon}
              left
              label="JOIN MEETING"
              onClick={() => window.open(values?.eventUrl ?? '')}
              className="flex-shrink-0 py-3"
              intent="secondary"
            />
          )}
          <div className="font-semibold text-xs text-content truncate max-w-[300px]">
            {values?.eventUrl}
          </div>
          <div className="flex items-center gap-2 justify-end flex-1">
            <button
              type="button"
              className=""
              onClick={() => copyToClipboard(values?.eventUrl ?? '')}
            >
              <MdContentCopy className="w-5 h-5 text-secondary " />
            </button>
            <IoMdClose
              role="button"
              onClick={() => setFieldValue('eventUrl', '')}
              className="w-5 h-5 text-content"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AddLink;
